import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Save, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OpenStatusPill } from "@/components/districts/OpenStatusPill";
import {
  DAY_KEYS,
  DAY_LABELS,
  type DayHours,
  type DayKey,
  type SpecialHour,
  emptyWeek,
  helsinkiNow,
} from "@/lib/opening-hours";
import {
  listManageableVenues,
  updateTodayHours,
  updateVenueInfo,
  type DistrictVenueDTO,
} from "@/lib/districts.functions";
import { PlaceMenusManager } from "@/components/menus/PlaceMenusManager";
import { SpecialHoursEditor } from "@/components/menus/VenueHoursFields";

export const Route = createFileRoute("/_authenticated/venue-manager")({
  head: () => ({
    meta: [
      { title: "Venue Manager — Hours & Menus | what Finland" },
      { name: "description", content: "Keep your opening hours, kitchen and lunch times, closures and menus up to date." },
      { property: "og:title", content: "Venue Manager — what Finland" },
      { property: "og:description", content: "Update your venue's hours, closures and menus in seconds." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VenueManager,
});

type WeekState = Record<DayKey, DayHours>;

function toWeek(src: unknown): WeekState {
  const base = emptyWeek();
  if (src && typeof src === "object") {
    for (const key of DAY_KEYS) {
      const found = Object.entries(src as Record<string, DayHours>).find(
        ([k]) => k.slice(0, 3).toLowerCase() === key.toLowerCase(),
      );
      if (found?.[1]) base[key] = { ...base[key], ...found[1] };
    }
  }
  return base;
}

function VenueManager() {
  const listFn = useServerFn(listManageableVenues);
  const { data, isPending } = useQuery({
    queryKey: ["manageable-venues"],
    queryFn: () => listFn(),
  });

  const venues = data?.venues ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && venues.length > 0) setSelectedId(venues[0]!.id);
  }, [venues, selectedId]);

  const selected = venues.find((v) => v.id === selectedId) ?? null;

  if (isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-navy/40" />
      </div>
    );
  }

  if (venues.length === 0) {
    const staff = !!data?.siteAdmin;
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <Store className="mx-auto size-8 text-navy/30" />
        <h1 className="mt-4 font-heading text-2xl text-navy">
          {staff ? "Use Business Admin for menus" : "No restaurant connected yet"}
        </h1>
        <p style={{ textTransform: "none" }} className="mt-2 text-sm leading-relaxed text-navy/60">
          {staff
            ? "This page is for partners whose restaurant is linked to their login. Staff add lunch, drinks and main menus in Business Admin → Menus."
            : "Venue Manager only shows restaurants linked to your partner account. Open your listing and use the Menus tab, or ask us to connect the venue."}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {staff ? (
            <Button asChild>
              <Link to="/business-admin" search={{ tab: "menus" }}>
                Open restaurant menus
              </Link>
            </Button>
          ) : (
            <Button asChild>
              <Link to="/partner">Open partner listings</Link>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:px-6 sm:py-12">
      <h1 className="font-heading text-2xl text-navy sm:text-3xl">Venue Manager</h1>
      <p style={{ textTransform: "none" }} className="mt-1 text-sm text-navy/60">
        Update opening hours, kitchen and lunch times, closures and menus. Changes go live immediately.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-2">
          {venues.map((v) => {
            const active = v.id === selectedId;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setSelectedId(v.id)}
                className={`w-full rounded-xl border p-3 text-left transition-colors ${
                  active ? "border-navy bg-navy text-white" : "border-navy/12 bg-white hover:border-navy/35"
                }`}
              >
                <span className="block font-heading text-sm leading-tight">{v.name}</span>
                <span
                  style={{ textTransform: "none" }}
                  className={`mt-1 block text-[11px] ${active ? "text-white/70" : "text-navy/50"}`}
                >
                  {v.address ?? v.area ?? "—"}
                </span>
              </button>
            );
          })}
        </aside>

        {selected ? <VenueEditor key={selected.id} venue={selected} /> : null}
      </div>
    </div>
  );
}

function VenueEditor({ venue }: { venue: DistrictVenueDTO }) {
  const qc = useQueryClient();
  const saveFn = useServerFn(updateVenueInfo);
  const todayFn = useServerFn(updateTodayHours);

  const [weekly, setWeekly] = useState<WeekState>(() => toWeek(venue.opening_hours));
  const [kitchen, setKitchen] = useState<WeekState>(() => toWeek(venue.kitchen_hours));
  const [lunch, setLunch] = useState<WeekState>(() => toWeek(venue.lunch_hours));
  const [tempClosed, setTempClosed] = useState(!!venue.temporarily_closed);
  const [tempNote, setTempNote] = useState(venue.temporarily_closed_note ?? "");
  const [special, setSpecial] = useState<SpecialHour[]>(
    Array.isArray(venue.special_hours) ? (venue.special_hours as SpecialHour[]) : [],
  );
  const [phone, setPhone] = useState(venue.phone ?? "");
  const [email, setEmail] = useState(venue.email ?? "");

  const preview = useMemo(
    () => ({
      opening_hours: weekly,
      kitchen_hours: kitchen,
      lunch_hours: lunch,
      special_hours: special,
      temporarily_closed: tempClosed,
      temporarily_closed_note: tempNote,
    }),
    [weekly, kitchen, lunch, special, tempClosed, tempNote],
  );

  const save = useMutation({
    mutationFn: () =>
      saveFn({
        data: {
          placeId: venue.id,
          opening_hours: weekly,
          kitchen_hours: kitchen,
          lunch_hours: lunch,
          special_hours: special,
          temporarily_closed: tempClosed,
          temporarily_closed_note: tempNote || null,
          phone: phone || null,
          email: email || null,
        },
      }),
    onSuccess: () => {
      toast.success("Saved — your listing is updated");
      qc.invalidateQueries({ queryKey: ["manageable-venues"] });
      qc.invalidateQueries({ queryKey: ["public-places"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const closeToday = useMutation({
    mutationFn: (closed: boolean) =>
      todayFn({
        data: {
          placeId: venue.id,
          date: helsinkiNow().isoDate,
          closed,
          note: closed ? "Closed today" : null,
        },
      }),
    onSuccess: () => {
      toast.success("Today's hours updated");
      qc.invalidateQueries({ queryKey: ["manageable-venues"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-navy">{venue.name}</h2>
          <p style={{ textTransform: "none" }} className="mt-1 text-xs text-navy/50">
            {venue.info_updated_at
              ? `Last updated ${new Date(venue.info_updated_at).toLocaleDateString()}`
              : "Never updated"}
          </p>
        </div>
        <OpenStatusPill source={preview} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => closeToday.mutate(true)} disabled={closeToday.isPending}>
          Closed today
        </Button>
        <Button size="sm" variant="outline" onClick={() => closeToday.mutate(false)} disabled={closeToday.isPending}>
          Open as usual today
        </Button>
      </div>

      <Tabs defaultValue="hours" className="mt-6">
        <TabsList>
          <TabsTrigger value="hours">Hours</TabsTrigger>
          <TabsTrigger value="kitchen">Kitchen & lunch</TabsTrigger>
          <TabsTrigger value="menu">Menu & contact</TabsTrigger>
        </TabsList>

        <TabsContent value="hours" className="pt-4">
          <WeekEditor value={weekly} onChange={setWeekly} />
          <div className="mt-5 rounded-xl border border-navy/10 p-4">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="temp-closed" style={{ textTransform: "none" }}>
                Temporarily closed
              </Label>
              <Switch id="temp-closed" checked={tempClosed} onCheckedChange={setTempClosed} />
            </div>
            {tempClosed ? (
              <Input
                className="mt-3"
                placeholder="Reason shown to guests (e.g. renovation until 15 May)"
                value={tempNote}
                onChange={(e) => setTempNote(e.target.value)}
              />
            ) : null}
          </div>
          <div className="mt-5">
            <SpecialHoursEditor value={special} onChange={setSpecial} />
          </div>
        </TabsContent>

        <TabsContent value="kitchen" className="pt-4 space-y-6">
          <div>
            <h3 className="mb-2 font-heading text-sm text-navy">Kitchen hours</h3>
            <WeekEditor value={kitchen} onChange={setKitchen} />
          </div>
          <div>
            <h3 className="mb-2 font-heading text-sm text-navy">Lunch hours</h3>
            <WeekEditor value={lunch} onChange={setLunch} />
          </div>
        </TabsContent>

        <TabsContent value="menu" className="pt-4 space-y-6">
          <PlaceMenusManager placeId={venue.id} />
          <div className="space-y-4 border-t border-navy/10 pt-4">
            <Field label="Phone" value={phone} onChange={setPhone} placeholder="+358 …" />
            <Field label="Email" value={email} onChange={setEmail} placeholder="hello@venue.fi" />
          </div>
        </TabsContent>

      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Save className="mr-2 size-4" />}
          Save changes
        </Button>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <Label style={{ textTransform: "none" }}>{label}</Label>
      <Input className="mt-1.5" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function WeekEditor({ value, onChange }: { value: WeekState; onChange: (v: WeekState) => void }) {
  const set = (day: DayKey, patch: Partial<DayHours>) => onChange({ ...value, [day]: { ...value[day], ...patch } });

  return (
    <div className="space-y-2">
      {DAY_KEYS.map((day) => {
        const d = value[day];
        const closed = !!d.closed;
        return (
          <div key={day} className="flex flex-wrap items-center gap-3 rounded-xl border border-navy/10 px-3 py-2">
            <span style={{ textTransform: "none" }} className="w-24 text-sm font-semibold text-navy">
              {DAY_LABELS[day]}
            </span>
            <label style={{ textTransform: "none" }} className="flex items-center gap-2 text-xs text-navy/60">
              <Switch checked={closed} onCheckedChange={(c) => set(day, { closed: c })} />
              Closed
            </label>
            {!closed ? (
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  className="h-9 w-28"
                  value={d.open ?? ""}
                  onChange={(e) => set(day, { open: e.target.value })}
                />
                <span className="text-navy/40">—</span>
                <Input
                  type="time"
                  className="h-9 w-28"
                  value={d.close ?? ""}
                  onChange={(e) => set(day, { close: e.target.value })}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
